import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Clock, FileText, Loader2, Send, Trash2 } from "lucide-react";
import { errText, fetchAgentOrders, fetchGuarantee, makeAgentOrder, proformaUrl, ron } from "@/lib/bf";
import BFListSalon from "./BFListSalon";
import BFProductSearch from "./BFProductSearch";
import BFAgentBrowse from "./BFAgentBrowse";
import BFFreeTotals from "./BFFreeTotals";

const PAY = [["cash", "Cash"], ["op", "Ordin de plată"], ["card", "Card"], ["ramburs", "Ramburs"]];
const FREE_PAY = ["cash", "op"];
const BIG = ["medavita", "framesi", "lalga", "mowan", "drbarrier", "cicalinic", "sturgeonbio"];
export const allowed = (brand, pay) => {
  if (brand === "winstory") return pay === "cash" ? ["0", "bf", "10", "20"] : ["0", "bf"];
  if (brand === "hhsimonsen") return ["0", "bf", "10", "20", "30", "35"];
  return ["0", "bf", "10", "20", "30", "40", ...(BIG.includes(brand) ? ["50"] : [])];
};
const lbl = (d) => (d === "bf" ? "Black Friday" : d === "0" ? "Fără discount" : `${d}%`);
const unitOf = (it, d) => (d === "bf" ? it.unit : Math.round((it.list || 0) * (100 - Number(d))) / 100);
const ST = { done: "proformă creată", pending: "așteaptă aprobarea adminului", rejected: "respinsă", deciding: "în aprobare" };

export default function BFAgentOrder({ cfg }) {
  const names = Object.fromEntries((cfg.brands || []).map((b) => [b.id, b.name]));
  const [salon, setSalon] = useState(null);
  const [pay, setPay] = useState("op");
  const [rows, setRows] = useState([]);
  const [disc, setDisc] = useState({});
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [hist, setHist] = useState([]);
  const [guar, setGuar] = useState(true);
  useEffect(() => { fetchAgentOrders().then(setHist).catch(() => {}); }, []);
  useEffect(() => {
    if (!salon) { setGuar(true); return; }
    fetchGuarantee(salon.id).then((r) => { setGuar(r.ok); if (!r.ok) setPay((p) => (FREE_PAY.includes(p) ? p : "op")); }).catch(() => setGuar(true));
  }, [salon]);
  const brands = [...new Set(rows.map((r) => r.brand))];
  const d = (b) => (allowed(b, pay).includes(disc[b] ?? "bf") ? disc[b] ?? "bf" : "bf");
  const choosePay = (k) => { setPay(k); setDisc((o) => Object.fromEntries(Object.entries(o).filter(([b, v]) => allowed(b, k).includes(v)))); };
  const priced = rows.map((r) => ({ ...r, unit: unitOf(r, d(r.brand)) }));
  const add = (o) => setRows((l) => (l.some((x) => x.key === o.key) ? l.map((x) => (x.key === o.key ? { ...x, q: x.q + 1 } : x)) : [...l, { ...o, q: 1 }]));
  const send = () => {
    if (!salon) return toast.error("Alege clientul");
    if (!rows.length) return toast.error("Adaugă cel puțin un produs");
    setBusy(true);
    makeAgentOrder({ lead: salon.id, payment: pay, items: rows.map((r) => ({ key: r.key, q: r.q })), disc: Object.fromEntries(brands.map((b) => [b, d(b)])), notes })
      .then((r) => { setHist((h) => [r, ...h]); setRows([]); setNotes(""); setDisc({}); toast.success(r.status === "pending" ? "Comanda așteaptă aprobarea adminului (discount 50%)" : `Proforma ${r.pf.no} a fost creată · ${ron(r.pf.total)}`); })
      .catch((e) => toast.error(errText(e, "Nu am putut trimite comanda"))).finally(() => setBusy(false));
  };
  return (
    <section className="invite-box bf-ao bf-rb" data-testid="bf-agent-order">
      <h3>Comandă agent</h3>
      <BFListSalon value={salon} onPick={setSalon} required />
      <div className="st-field"><span className="st-label">Metoda de plată</span>
        <div className="bfc-seg">{PAY.map(([k, l]) => <button key={k} type="button" className={pay === k ? "is-on" : ""} disabled={!guar && !FREE_PAY.includes(k)} onClick={() => choosePay(k)} data-testid={`bf-ao-pay-${k}`}>{l}</button>)}</div>
        {!guar && <p className="bf-fid-state" data-testid="bf-ao-no-guarantee"><AlertTriangle size={14} /> Clientul nu are contractul cu fidejusiune semnat la toate 3 firmele: doar cash sau ordin de plată, plătit înainte de livrare.</p>}</div>
      <BFProductSearch onAdd={add} />
      <BFAgentBrowse cfg={cfg} onAdd={(o) => { add(o); toast.success(`Adăugat: ${o.name}`, { duration: 1200 }); }} />
      {rows.length > 0 && (
        <div className="bf-rb-table"><table>
          <thead><tr><th>Buc.</th><th>Produs</th><th>Preț</th><th>Total</th><th /></tr></thead>
          <tbody>{priced.map((r, i) => (
            <tr key={r.key} data-testid={`bf-ao-row-${i}`}>
              <td><input type="number" min="1" value={r.q} onChange={(e) => setRows((l) => l.map((x) => (x.key === r.key ? { ...x, q: Math.max(1, +e.target.value || 1) } : x)))} data-testid={`bf-ao-q-${i}`} /></td>
              <td>{r.name}<small>cod {r.code} · listă {ron(r.list)}</small></td>
              <td className="bf-rb-sum">{ron(r.unit)}</td><td className="bf-rb-sum">{ron(r.unit * r.q)}</td>
              <td><button type="button" onClick={() => setRows((l) => l.filter((x) => x.key !== r.key))} aria-label="Șterge" data-testid={`bf-ao-del-${i}`}><Trash2 size={14} /></button></td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
      {brands.length > 0 && (
        <div className="bf-ao-disc" data-testid="bf-ao-disc">
          {brands.map((b) => (
            <div key={b} className="bf-ao-disc-row"><b>{names[b] || b}</b>
              <div className="bfc-seg">{allowed(b, pay).map((x) => <button key={x} type="button" className={d(b) === x ? "is-on" : ""} onClick={() => setDisc({ ...disc, [b]: x })} data-testid={`bf-ao-disc-${b}-${x}`}>{lbl(x)}</button>)}</div>
            </div>
          ))}
          <p className="st-hint">Până la 40% proforma se face imediat. La 50% comanda așteaptă aprobarea adminului. Winstory: maxim 20% doar la cash. HH Simonsen: maxim 35%.</p>
        </div>
      )}
      <BFFreeTotals ok={priced} />
      <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Observații (opțional)" className="bf-ao-notes" data-testid="bf-ao-notes" />
      <button type="button" className="st-btn st-btn--silver" onClick={send} disabled={busy} data-testid="bf-ao-send">{busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Trimite comanda</button>
      {hist.length > 0 && (
        <ul className="bfc-orders bf-ao-hist" data-testid="bf-ao-history">
          {hist.slice(0, 20).map((h) => (
            <li key={h.id} data-testid={`bf-ao-hist-${h.id}`}>
              <b>{h.salon}</b><small>{new Date(h.created_at).toLocaleString("ro-RO", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} · {ST[h.status] || h.status}</small><span>{ron(h.total)}</span>
              {h.pf ? <a className="st-btn st-btn--ghost" href={`${proformaUrl(h.pf.id)}.xlsx`}><FileText size={14} /> {h.pf.no}</a> : <span className="bf-ao-wait"><Clock size={14} /></span>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
